
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Users, Eye, Clock, TrendingDown, Globe, Smartphone, Monitor, Tablet } from 'lucide-react'

interface SiteAnalyticsOverviewProps {
  totalVisits: number
  uniqueVisitors: number
  avgSessionDuration: number
  bounceRate: number
  realTimeVisitors: number
  deviceBreakdown: Array<{
    device_type: string
    count: number
    percentage: number
  }>
}

export const SiteAnalyticsOverview = ({
  totalVisits,
  uniqueVisitors,
  avgSessionDuration,
  bounceRate,
  realTimeVisitors,
  deviceBreakdown
}: SiteAnalyticsOverviewProps) => {
  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}m ${remainingSeconds.toFixed(0)}s`
  }

  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType) {
      case 'desktop': return <Monitor className="h-4 w-4" />
      case 'mobile': return <Smartphone className="h-4 w-4" />
      case 'tablet': return <Tablet className="h-4 w-4" />
      default: return <Globe className="h-4 w-4" />
    }
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6">
      {/* Real-time visitors */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium">Live Visitors</CardTitle>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <Users className="h-4 w-4 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-green-600">{realTimeVisitors}</div>
          <p className="text-xs text-muted-foreground">Active now</p>
        </CardContent>
      </Card>

      {/* Total visits */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium">Total Visits</CardTitle>
          <Eye className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{totalVisits.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">Page views</p>
        </CardContent>
      </Card>

      {/* Unique visitors */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium">Unique Visitors</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{uniqueVisitors.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">Individual users</p>
        </CardContent>
      </Card>

      {/* Average session duration */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium">Avg. Session</CardTitle>
          <Clock className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{formatDuration(avgSessionDuration)}</div>
          <p className="text-xs text-muted-foreground">Time on site</p>
        </CardContent>
      </Card>

      {/* Bounce rate */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium">Bounce Rate</CardTitle>
          <TrendingDown className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{bounceRate.toFixed(1)}%</div>
          <p className="text-xs text-muted-foreground">Single page visits</p>
        </CardContent>
      </Card>

      {/* Device breakdown card spanning multiple columns */}
      <Card className="md:col-span-2 xl:col-span-5">
        <CardHeader>
          <CardTitle className="text-lg">Device Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            {deviceBreakdown.map((device) => (
              <div key={device.device_type} className="flex items-center gap-2">
                {getDeviceIcon(device.device_type)}
                <span className="text-sm font-medium capitalize">
                  {device.device_type}
                </span>
                <Badge variant="secondary">
                  {device.count} ({device.percentage.toFixed(1)}%)
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
